/**
 * Studio content-binding selectors (Build Engine Selector v1, Stream C).
 *
 * A node never carries literal copy for approved content — it carries a
 * `contentRef` naming a key in `section.content.fields`, with three canonical
 * shortcuts ('headline' | 'body' | 'image') pointing at the section's own
 * `headline`/`body`/`imageUrl`. Text may contain {{binding}} tokens; display
 * resolution happens inside `EditableText`/`useBindings`, exactly like every
 * other momentum_v2 archetype — these selectors only pick the RAW string.
 *
 * `AudienceCard`/`TacticCard` contentRefs name campaign objects directly
 * (`audience.segment.<n>` / `tactic:<productId>`) rather than free-form
 * fields; the card components read the segment/tactic from proposal state
 * the way the existing momentum-v2 pages do (audiencePresentation.ts,
 * lib/plan.ts), with a `fields` override winning for editable copy.
 */

import type { AudienceSegment, DocumentSection, Proposal, TacticStrategy } from '@shared/types';
import type { SceneNodeProps } from '../../../../src/build-engines/studio/types.js';
import type { SelectionField } from '../../state/useSelectionContext';
import { audienceCards, type AudienceCardViewModel } from '../../lib/audiencePresentation';
import { productLabel } from '../../lib/plan';

/** The three shortcuts a node's `contentRef` may name instead of a `fields` key. */
export type CanonicalContentRef = 'headline' | 'body' | 'image';

export function isCanonicalContentRef(ref: string | undefined): ref is CanonicalContentRef {
  return ref === 'headline' || ref === 'body' || ref === 'image';
}

/**
 * Raw (unresolved — may still contain {{binding}} tokens) text for a text
 * node: `fields[contentRef]` first, then the canonical headline/body
 * shortcut, then the agent-authored literal `props.text`, else ''.
 */
export function resolveNodeText(section: DocumentSection, props: SceneNodeProps | undefined): string {
  const contentRef = props?.contentRef;
  if (contentRef) {
    const fromFields = section.content.fields?.[contentRef];
    if (fromFields !== undefined) return fromFields;
    if (contentRef === 'headline') return section.content.headline ?? props?.text ?? '';
    if (contentRef === 'body') return section.content.body ?? props?.text ?? '';
  }
  return props?.text ?? '';
}

/** Raw image URL for an Image/HeroImage/DeviceMockup/Logo node. */
export function resolveNodeImageUrl(section: DocumentSection, props: SceneNodeProps | undefined): string | undefined {
  const contentRef = props?.contentRef;
  if (contentRef) {
    const fromFields = section.content.fields?.[contentRef];
    if (fromFields) return fromFields;
    if (contentRef === 'image') return section.content.imageUrl ?? undefined;
  }
  return section.content.imageUrl ?? undefined;
}

/**
 * The `SelectionField` a text node's edits are attributed to — the chat
 * context and the selection chip key off this the same way every other
 * momentum_v2 archetype does (Editable.tsx `SelectionField`: 'headline' |
 * 'body' | ... | `fields.<key>`).
 */
export function selectionFieldFor(contentRef: string | undefined): SelectionField {
  if (contentRef === 'headline') return 'headline';
  if (contentRef === 'body') return 'body';
  return `fields.${contentRef ?? 'text'}` as SelectionField;
}

/** Builds the `saveContent` patch for a text node's contentRef. */
export function contentPatchFor(
  section: DocumentSection,
  contentRef: string | undefined,
  next: string,
): Parameters<import('../types').PageActions['saveContent']>[1] {
  if (contentRef === 'headline') return { headline: next };
  if (contentRef === 'body') return { body: next };
  const key = contentRef ?? 'text';
  return { fields: { ...section.content.fields, [key]: next } };
}

/** Builds the `saveContent` patch for an Image/HeroImage node's contentRef. */
export function imagePatchFor(
  section: DocumentSection,
  contentRef: string | undefined,
  url: string,
): Parameters<import('../types').PageActions['saveContent']>[1] {
  if (contentRef === 'image' || !contentRef) return { imageUrl: url };
  return { fields: { ...section.content.fields, [contentRef]: url } };
}

// ---------------------------------------------------------------------------
// AudienceCard: contentRef 'audience.segment.<n>' (1-indexed, matching the
// deck's own persona numbering).
// ---------------------------------------------------------------------------

const AUDIENCE_SEGMENT_REF = /^audience\.segment\.(\d+)$/;

export function audienceCardForContentRef(
  proposal: Proposal,
  contentRef: string | undefined,
): AudienceCardViewModel | null {
  if (!contentRef) return null;
  const match = AUDIENCE_SEGMENT_REF.exec(contentRef);
  if (!match) return null;
  const index = Number(match[1]) - 1;
  const cards = audienceCards(proposal.audience);
  return cards[index] ?? null;
}

export function audienceSegmentPatch(
  audience: Proposal['audience'],
  segmentId: string,
  patch: Partial<AudienceSegment>,
): AudienceSegment[] {
  return audience.segments.map((segment) => (segment.id === segmentId ? { ...segment, ...patch } : segment));
}

// ---------------------------------------------------------------------------
// TacticCard: contentRef 'tactic:<productId>'.
// ---------------------------------------------------------------------------

export function tacticForContentRef(proposal: Proposal, contentRef: string | undefined): TacticStrategy | null {
  if (!contentRef?.startsWith('tactic:')) return null;
  const productId = contentRef.slice('tactic:'.length);
  return proposal.strategy.tactics.find((tactic) => tactic.productId === productId) ?? null;
}

export function tacticLabel(proposal: Proposal, productId: string): string {
  return productLabel(proposal, productId);
}
